import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Users, Plus, Search, ChevronDown, ChevronUp, X, Tag } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const lbl  = { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 };
const inp  = { width: '100%', padding: '8px 10px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 13, color: '#111827', background: '#fff', boxSizing: 'border-box' };
const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', userSelect: 'none' };

// ─── Onglet Personnes ─────────────────────────────────────────────────────────

// Raisons d'exclusion → libellés lisibles
const REASON_LABELS = {
  NO_EMAIL:                    'Pas d\'email',
  NO_TIERS:                    'Aucun tiers lié',
  COOLDOWN_ACTIVE:             'Intervalle actif',
  ECHEANCE_NOT_REACHED:        'Échéance non atteinte',
  ACCOUNTING_CREDIT_NON_LETTRE:'Crédit non lettré',
  ACCOUNTING_CREDIT_BALANCE:   'Solde créditeur',
};

// Tri naturel sur une clé
function naturalSort(arr, key, dir) {
  return [...arr].sort((a, b) => {
    const va = String(a[key] ?? '');
    const vb = String(b[key] ?? '');
    const cmp = va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  });
}

function SortTh({ label, sortKey, sortConfig, onSort, color, borderColor }) {
  const active = sortConfig.key === sortKey;
  const icon = active ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ' ⇅';
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{
        padding: '7px 12px', fontWeight: 600, color: color || '#6B7280',
        textAlign: 'left', borderBottom: `1px solid ${borderColor || '#E5E7EB'}`,
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      {label}<span style={{ opacity: active ? 1 : 0.4, fontSize: 10 }}>{icon}</span>
    </th>
  );
}

function PersonnesTab({ audienceId, handler }) {
  const isRelance = handler === 'relance';
  const [membres,    setMembres]    = useState([]);
  const [preview,    setPreview]    = useState(null);   // { recipients, exclusions } pour relance
  const [loading,    setLoading]    = useState(false);
  const [search,     setSearch]     = useState('');
  const [subTab,     setSubTab]     = useState('eligibles');  // 'eligibles' | 'exclus'
  const [sortElig,   setSortElig]   = useState({ key: 'nom', direction: 'asc' });
  const [sortExcl,   setSortExcl]   = useState({ key: 'nom', direction: 'asc' });

  const handleSortElig = key => setSortElig(prev =>
    prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
  );
  const handleSortExcl = key => setSortExcl(prev =>
    prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
  );

  useEffect(() => {
    if (!audienceId) return;
    setLoading(true);
    if (isRelance) {
      api.getAudiencePreview(audienceId)
        .then(r => setPreview(r.data || { recipients: [], exclusions: [] }))
        .catch(() => toast.error('Erreur chargement preview'))
        .finally(() => setLoading(false));
    } else {
      api.getAudienceMembres(audienceId)
        .then(r => setMembres(r.data || []))
        .catch(() => toast.error('Erreur chargement contacts'))
        .finally(() => setLoading(false));
    }
  }, [audienceId, isRelance]);

  const eligibles  = useMemo(() => preview?.recipients  || [], [preview]);
  const exclusions = useMemo(() => preview?.exclusions  || [], [preview]);

  const filteredMembres = useMemo(() => {
    if (!search.trim()) return membres;
    const q = search.toLowerCase();
    return membres.filter(p =>
      (p.nom || '').toLowerCase().includes(q) ||
      (p.prenom || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.entreprises || '').toLowerCase().includes(q)
    );
  }, [membres, search]);

  const filteredEligibles = useMemo(() => {
    const base = !search.trim() ? eligibles : (() => {
      const q = search.toLowerCase();
      return eligibles.filter(r =>
        (r.nom || '').toLowerCase().includes(q) ||
        (r.prenom || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.tiers_nom || '').toLowerCase().includes(q)
      );
    })();
    return naturalSort(base, sortElig.key, sortElig.direction);
  }, [eligibles, search, sortElig]);

  const filteredExclus = useMemo(() => {
    const base = !search.trim() ? exclusions : (() => {
      const q = search.toLowerCase();
      return exclusions.filter(r =>
        (r.nom    || '').toLowerCase().includes(q) ||
        (r.prenom || '').toLowerCase().includes(q) ||
        (r.tiers_nom || '').toLowerCase().includes(q) ||
        (r.detail || '').toLowerCase().includes(q)
      );
    })();
    return naturalSort(base, sortExcl.key, sortExcl.direction);
  }, [exclusions, search, sortExcl]);

  if (!audienceId) return (
    <p style={{ color: '#9CA3AF', fontSize: 13, fontStyle: 'italic', padding: '24px 0' }}>
      Sauvegardez l'audience pour voir les contacts résolus.
    </p>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Barre de recherche */}
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email, tiers…"
          style={{ ...inp, paddingLeft: 32 }}
          data-testid="personnes-search"
        />
        {search && (
          <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
            <X size={13} />
          </button>
        )}
      </div>

      {/* Vue Relance : deux sous-onglets Éligibles / Exclus */}
      {isRelance && !loading && (
        <>
          <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #E5E7EB', paddingBottom: 0 }}>
            {[
              { key: 'eligibles', label: `Éligibles (${eligibles.length})`, color: '#15803D', bg: '#F0FDF4', border: '#BBF7D0' },
              { key: 'exclus',    label: `Exclus (${exclusions.length})`,   color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' },
            ].map(({ key, label, color, bg, border }) => (
              <button key={key} onClick={() => setSubTab(key)}
                data-testid={`subtab-${key}`}
                style={{
                  padding: '6px 14px', borderRadius: '6px 6px 0 0', fontSize: 12, fontWeight: 700,
                  cursor: 'pointer', border: `1px solid ${subTab === key ? border : '#E5E7EB'}`,
                  borderBottom: subTab === key ? 'none' : undefined,
                  background: subTab === key ? bg : '#F9FAFB',
                  color: subTab === key ? color : '#6B7280',
                  marginBottom: subTab === key ? -1 : 0,
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* Table Éligibles */}
          {subTab === 'eligibles' && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '0 6px 6px 6px', overflow: 'hidden' }}>
              <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                {filteredEligibles.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9CA3AF', padding: '16px 14px', margin: 0 }}>Aucun contact éligible.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ background: '#F0FDF4', position: 'sticky', top: 0, zIndex: 1 }}>
                      {[
                        { label: 'Nom',    key: 'nom'      },
                        { label: 'Prénom', key: 'prenom'   },
                        { label: 'Email',  key: 'email'    },
                        { label: 'Tiers',  key: 'tiers_nom'},
                      ].map(col => (
                        <SortTh key={col.key} label={col.label} sortKey={col.key}
                          sortConfig={sortElig} onSort={handleSortElig}
                          color="#15803D" borderColor="#BBF7D0" />
                      ))}
                    </tr></thead>
                    <tbody>
                      {filteredEligibles.map((r, i) => (
                        <tr key={`${r.personne_id}-${r.tiers_id}`} style={{ background: i % 2 === 0 ? '#fff' : '#F9FFF9', borderTop: '1px solid #F0FDF4' }}>
                          <td style={{ padding: '6px 12px', fontWeight: 500 }}>{r.nom || '—'}</td>
                          <td style={{ padding: '6px 12px' }}>{r.prenom || '—'}</td>
                          <td style={{ padding: '6px 12px', color: '#2563EB' }}>{r.email}</td>
                          <td style={{ padding: '6px 12px', color: '#374151' }}>{r.tiers_nom || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* Table Exclus */}
          {subTab === 'exclus' && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: '0 6px 6px 6px', overflow: 'hidden' }}>
              <div style={{ maxHeight: 380, overflowY: 'auto' }}>
                {filteredExclus.length === 0 ? (
                  <p style={{ fontSize: 12, color: '#9CA3AF', padding: '16px 14px', margin: 0 }}>Aucun contact exclu.</p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead><tr style={{ background: '#FFFBEB', position: 'sticky', top: 0, zIndex: 1 }}>
                      {[
                        { label: 'Contact', key: 'nom'      },
                        { label: 'Tiers',   key: 'tiers_nom'},
                        { label: 'Raison',  key: 'reason'   },
                        { label: 'Détail',  key: 'detail'   },
                      ].map(col => (
                        <SortTh key={col.key} label={col.label} sortKey={col.key}
                          sortConfig={sortExcl} onSort={handleSortExcl}
                          color="#B45309" borderColor="#FDE68A" />
                      ))}
                    </tr></thead>
                    <tbody>
                      {filteredExclus.map((r, i) => (
                        <tr key={`${r.personne_id}-${r.tiers_id || i}`} style={{ background: i % 2 === 0 ? '#fff' : '#FFFDF5', borderTop: '1px solid #FFFBEB' }}>
                          <td style={{ padding: '6px 12px', fontWeight: 500 }}>
                            {[r.nom, r.prenom].filter(Boolean).join(' ') || r.personne_id}
                          </td>
                          <td style={{ padding: '6px 12px', color: '#374151' }}>{r.tiers_nom || '—'}</td>
                          <td style={{ padding: '6px 12px' }}>
                            <span style={{ background: '#FEF3C7', color: '#92400E', padding: '1px 7px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {REASON_LABELS[r.reason] || r.reason}
                            </span>
                          </td>
                          <td style={{ padding: '6px 12px', color: '#6B7280', fontSize: 11 }}>{r.detail || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Vue Standard */}
      {!isRelance && (
        <>
          <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>
            {loading ? 'Chargement…' : `${filteredMembres.length} contact${filteredMembres.length !== 1 ? 's' : ''}${search ? ` sur ${membres.length}` : ''}`}
          </p>
          {!loading && filteredMembres.length > 0 && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 7, overflow: 'hidden' }}>
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB', position: 'sticky', top: 0, zIndex: 1 }}>
                      {['Nom', 'Prénom', 'Email', 'Entreprise(s)'].map(h => (
                        <th key={h} style={{ padding: '7px 12px', fontWeight: 600, color: '#6B7280', textAlign: 'left', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembres.map((p, i) => (
                      <tr key={p.personne_id} style={{ borderTop: i > 0 ? '1px solid #F3F4F6' : 'none', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                        <td style={{ padding: '6px 12px', fontWeight: 500 }}>{p.nom || '—'}</td>
                        <td style={{ padding: '6px 12px' }}>{p.prenom || '—'}</td>
                        <td style={{ padding: '6px 12px', color: p.email ? '#2563EB' : '#9CA3AF' }}>{p.email || '—'}</td>
                        <td style={{ padding: '6px 12px', color: '#6B7280' }}>{p.entreprises || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!loading && filteredMembres.length === 0 && (
            <p style={{ fontSize: 12, color: '#9CA3AF', fontStyle: 'italic', margin: 0 }}>
              {search ? 'Aucun résultat pour cette recherche.' : 'Aucun contact dans cette audience.'}
            </p>
          )}
        </>
      )}
    </div>
  );
}

// ─── Side Panel ───────────────────────────────────────────────────────────────

function AudienceSidePanel({ initial, onSave, onDelete, onClose, tagsFonction, tagsAutomation }) {
  const isNew       = !initial?.audience_id;
  const [tab,       setTab]       = useState('params');
  const [name,      setName]      = useState(initial?.audience_name || '');
  const [desc,      setDesc]      = useState(initial?.description   || '');
  const [selTags,   setSelTags]   = useState([]);
  const [selected,  setSelected]  = useState([]);   // contacts manuels
  const [search,    setSearch]    = useState('');
  const [results,   setResults]   = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const [showFn,    setShowFn]    = useState(true);
  const [showAuto,  setShowAuto]  = useState(true);
  // Filtres (section Relance)
  const [handler,            setHandler]            = useState('');
  const [exclureCreditNl,    setExclureCreditNl]    = useState(true);
  const [exclureSoldeCr,     setExclureSoldeCr]     = useState(true);
  const [echeanceJours,      setEcheanceJours]      = useState('');
  const [intervalleJours,    setIntervalleJours]    = useState('');
  const searchRef = useRef();

  // Chargement initial (édition)
  useEffect(() => {
    if (!initial?.audience_id) return;
    const fr     = initial?.filter_rules || {};
    const tagIds = fr.tag_ids || [];
    if (tagIds.length) {
      const all = [...(tagsFonction || []), ...(tagsAutomation || [])];
      setSelTags(all.filter(t => tagIds.includes(t.tag_id)));
    }
    setHandler(fr.handler || '');
    const af = fr.accounting_filters || {};
    setExclureCreditNl(af.exclure_credit_non_lettre !== false);
    setExclureSoldeCr(af.exclure_solde_crediteur   !== false);
    setEcheanceJours(af.echeance_depassee_jours   != null ? String(af.echeance_depassee_jours)   : '');
    setIntervalleJours(af.intervalle_relance_jours != null ? String(af.intervalle_relance_jours) : '');
    api.getAudienceLignes(initial.audience_id)
      .then(r => setSelected(r.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?.audience_id]);

  // Recherche contacts
  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.searchPersonnes(search);
        const already = new Set(selected.map(p => p.personne_id));
        setResults((r.data || []).filter(p => !already.has(p.personne_id)));
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [search, selected]);

  const addPersonne    = p => { setSelected(s => [...s, p]); setSearch(''); setResults([]); };
  const removePersonne = id => setSelected(s => s.filter(p => p.personne_id !== id));
  const toggleTag      = t  => setSelTags(s => s.find(x => x.tag_id === t.tag_id) ? s.filter(x => x.tag_id !== t.tag_id) : [...s, t]);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      const mode = selTags.length > 0 ? 'tags' : 'manual';
      const af = handler === 'relance' ? {
        exclure_credit_non_lettre: exclureCreditNl,
        exclure_solde_crediteur:   exclureSoldeCr,
        ...(echeanceJours   !== '' ? { echeance_depassee_jours:   parseInt(echeanceJours,   10) } : {}),
        ...(intervalleJours !== '' ? { intervalle_relance_jours:  parseInt(intervalleJours, 10) } : {}),
      } : {};
      const filter_rules = {
        type:    mode,
        ...(mode === 'tags' ? { tag_ids: selTags.map(t => t.tag_id) } : {}),
        ...(handler ? { handler } : {}),
        ...(handler === 'relance' ? { accounting_filters: af } : {}),
      };
      await onSave(initial?.audience_id, {
        audience_name: name.trim(),
        description:   desc.trim() || null,
        filter_rules,
        personne_ids:  selected.map(p => p.personne_id),
      });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette audience ?')) return;
    setDeleting(true);
    try { await onDelete(initial.audience_id); }
    finally { setDeleting(false); }
  };

  const allTags = [
    { group: 'Fonctions', tags: tagsFonction || [] },
    { group: 'Automation', tags: tagsAutomation || [] },
  ];

  const tabStyle = active => ({
    padding: '8px 16px', borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
    color: active ? '#2563EB' : '#6B7280', fontWeight: active ? 600 : 400,
    fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottomStyle: 'solid',
  });

  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 1040, background: '#fff',
      boxShadow: '-4px 0 24px rgba(0,0,0,0.10)', zIndex: 50, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #F3F4F6' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>
            {isNew ? 'Nouvelle audience' : name || 'Audience'}
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' }}>
            <X size={18} />
          </button>
        </div>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0 }}>
          <button style={tabStyle(tab === 'params')} onClick={() => setTab('params')} data-testid="tab-params">
            Paramètres
          </button>
          <button style={tabStyle(tab === 'personnes')} onClick={() => setTab('personnes')} data-testid="tab-personnes"
            disabled={isNew}>
            Personnes{!isNew ? ` (${initial?.nb_contacts ?? 0})` : ''}
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

        {tab === 'params' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Nom */}
            <div>
              <div style={lbl}>Nom de l'audience *</div>
              <input value={name} onChange={e => setName(e.target.value)} style={inp}
                placeholder="Ex: Clients Guadeloupe" data-testid="audience-nom" />
            </div>

            {/* Description */}
            <div>
              <div style={lbl}>Description</div>
              <textarea value={desc} onChange={e => setDesc(e.target.value)} style={{ ...inp, height: 60, resize: 'vertical' }}
                placeholder="Usage, critères de sélection…" data-testid="audience-description" />
            </div>

            {/* Type d'audience */}
            <div>
              <div style={lbl}>Type d'audience</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[
                  { value: '',        label: 'Standard' },
                  { value: 'relance', label: 'Relance' },
                ].map(opt => (
                  <button key={opt.value} onClick={() => setHandler(opt.value)}
                    data-testid={`handler-${opt.value || 'standard'}`}
                    style={{
                      padding: '5px 14px', borderRadius: 99, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', border: 'none',
                      background: handler === opt.value ? '#1D4ED8' : '#F3F4F6',
                      color:      handler === opt.value ? '#fff'     : '#374151',
                    }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Filtres — visible uniquement pour Relance */}
            {handler === 'relance' && (
              <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ ...lbl, color: '#0369A1', marginBottom: 10 }}>Filtres</div>

                {/* Toggle : exclure crédit non lettré */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 8 }}>
                  <input type="checkbox" checked={exclureCreditNl} onChange={e => setExclureCreditNl(e.target.checked)}
                    data-testid="filter-credit-non-lettre"
                    style={{ accentColor: '#0369A1', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E' }}>Exclure si crédit non lettré</span>
                </label>

                {/* Toggle : exclure solde créditeur */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                  <input type="checkbox" checked={exclureSoldeCr} onChange={e => setExclureSoldeCr(e.target.checked)}
                    data-testid="filter-solde-crediteur"
                    style={{ accentColor: '#0369A1', flexShrink: 0 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E' }}>Exclure si solde global créditeur</span>
                </label>

                {/* Séparateur */}
                <div style={{ borderTop: '1px solid #BAE6FD', marginBottom: 12 }} />

                {/* Écheance dépassée de X jours */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E', flex: 1 }}>
                    Envoyer uniquement si échéance dépassée depuis
                  </span>
                  <input
                    type="number" min="0" value={echeanceJours}
                    onChange={e => setEcheanceJours(e.target.value)}
                    placeholder="—"
                    data-testid="filter-echeance-jours"
                    style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1px solid #BAE6FD',
                      fontSize: 13, textAlign: 'center', background: '#fff', color: '#0C4A6E' }}
                  />
                  <span style={{ fontSize: 12, color: '#0369A1' }}>jours</span>
                </div>

                {/* Intervalle minimum entre relances */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0C4A6E', flex: 1 }}>
                    Intervalle minimum entre relances
                  </span>
                  <input
                    type="number" min="0" value={intervalleJours}
                    onChange={e => setIntervalleJours(e.target.value)}
                    placeholder="—"
                    data-testid="filter-intervalle-jours"
                    style={{ width: 64, padding: '4px 8px', borderRadius: 6, border: '1px solid #BAE6FD',
                      fontSize: 13, textAlign: 'center', background: '#fff', color: '#0C4A6E' }}
                  />
                  <span style={{ fontSize: 12, color: '#0369A1' }}>jours</span>
                </div>

                <p style={{ fontSize: 11, color: '#0369A1', marginTop: 10, marginBottom: 0 }}>
                  Basé sur la synchronisation Pennylane (mise à jour quotidienne).
                </p>
              </div>
            )}

            {/* Tags */}
            {allTags.map(({ group, tags }) => tags.length > 0 && (
              <div key={group}>
                <button onClick={() => group === 'Fonctions' ? setShowFn(v => !v) : setShowAuto(v => !v)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 6 }}>
                  {(group === 'Fonctions' ? showFn : showAuto) ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  <span style={lbl}><Tag size={10} style={{ marginRight: 3 }} />{group}</span>
                </button>
                {(group === 'Fonctions' ? showFn : showAuto) && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {tags.map(t => {
                      const active = selTags.some(x => x.tag_id === t.tag_id);
                      return (
                        <button key={t.tag_id} onClick={() => toggleTag(t)}
                          style={{ ...chip, background: active ? '#DBEAFE' : '#F3F4F6', color: active ? '#1D4ED8' : '#374151',
                            outline: active ? '1px solid #93C5FD' : 'none' }}
                          data-testid={`tag-${t.tag_id}`}>
                          {t.nom}
                          {active && <X size={10} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            {/* Contacts manuels */}
            <div>
              <div style={lbl}>Contacts ajoutés manuellement</div>
              <div style={{ position: 'relative' }}>
                <Search size={13} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
                <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un contact…" style={{ ...inp, paddingLeft: 30 }}
                  data-testid="audience-search-contact" />
              </div>
              {/* Résultats */}
              {search.trim() && (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 6, marginTop: 4, overflow: 'hidden', maxHeight: 180, overflowY: 'auto' }}>
                  {searching && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF' }}>Recherche…</div>}
                  {!searching && results.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF' }}>Aucun résultat</div>}
                  {results.map(p => (
                    <button key={p.personne_id} onClick={() => addPersonne(p)}
                      style={{ width: '100%', padding: '7px 12px', textAlign: 'left', background: 'none', border: 'none',
                        borderTop: '1px solid #F3F4F6', cursor: 'pointer', fontSize: 12 }}>
                      <strong>{p.nom} {p.prenom}</strong>
                      {p.email && <span style={{ color: '#6B7280', marginLeft: 8 }}>{p.email}</span>}
                    </button>
                  ))}
                </div>
              )}
              {/* Chips sélectionnés */}
              {selected.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                  {selected.map(p => (
                    <span key={p.personne_id} style={{ ...chip, background: '#F0FDF4', color: '#166534', outline: '1px solid #BBF7D0' }}>
                      {p.nom} {p.prenom}
                      <button onClick={() => removePersonne(p.personne_id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#166534', padding: 0, lineHeight: 1 }}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Résumé */}
            <div style={{ background: '#F8FAFC', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#64748B', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Users size={13} />
              {selTags.length > 0
                ? <span><strong>{selTags.length} tag{selTags.length > 1 ? 's' : ''}</strong>
                    {selected.length > 0 && <> + <strong>{selected.length}</strong> manuel{selected.length > 1 ? 's' : ''}</>}
                    {' '}— doublons éliminés automatiquement</span>
                : <span>{selected.length} contact{selected.length !== 1 ? 's' : ''} manuel{selected.length !== 1 ? 's' : ''}</span>
              }
            </div>
          </div>
        )}

        {tab === 'personnes' && <PersonnesTab audienceId={initial?.audience_id} handler={handler} />}
      </div>

      {/* Footer */}
      <div style={{ padding: '16px 24px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <div>
          {!isNew && (
            <button onClick={handleDelete} disabled={deleting}
              style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #FECACA', background: '#FFF', color: '#DC2626', fontSize: 13, cursor: 'pointer' }}
              data-testid="audience-delete-btn">
              {deleting ? 'Suppression…' : 'Supprimer'}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#fff', fontSize: 13, cursor: 'pointer' }}>
            Annuler
          </button>
          <button onClick={handleSave} disabled={saving}
            style={{ padding: '8px 18px', borderRadius: 6, border: 'none', background: '#2563EB', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            data-testid="audience-save-btn">
            {saving ? 'Enregistrement…' : isNew ? 'Créer' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function EmailAudiences() {
  const [audiences,      setAudiences]      = useState([]);
  const [tagsFonction,   setTagsFonction]   = useState([]);
  const [tagsAutomation, setTagsAutomation] = useState([]);
  const [panel,          setPanel]          = useState(null);  // null | { mode:'new'|'edit', data }
  const [loading,        setLoading]        = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, tf, ta] = await Promise.all([
        api.getEmailAudiences(),
        api.getAnnuaireTagsByType('fonction'),
        api.getAnnuaireTagsByType('automation'),
      ]);
      setAudiences(a.data || []);
      setTagsFonction(tf.data  || []);
      setTagsAutomation(ta.data || []);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Synchronise le panel ouvert avec la liste fraîche (nb_contacts, etc.)
  useEffect(() => {
    if (!panel?.data?.audience_id) return;
    const fresh = audiences.find(a => a.audience_id === panel.data.audience_id);
    if (fresh) setPanel(prev => ({ ...prev, data: fresh }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audiences]);

  const handleSave = async (audienceId, payload) => {
    try {
      if (audienceId) {
        await api.updateEmailAudience(audienceId, payload);
        toast.success('Audience mise à jour');
        await load();
        // Rouvrir en édition pour refléter les nouvelles données (nb_contacts mis à jour)
        setPanel(prev => {
          if (!prev) return null;
          return { mode: 'edit', data: { ...prev.data, ...payload, audience_id: audienceId } };
        });
      } else {
        const res = await api.createEmailAudience(payload);
        toast.success('Audience créée');
        const newId = res.data?.audience_id;
        await load();
        // Rouvrir en édition avec le nouvel ID → active l'onglet Personnes
        if (newId) {
          setPanel({ mode: 'edit', data: { ...payload, audience_id: newId, nb_contacts: 0 } });
        } else {
          setPanel(null);
        }
      }
    } catch { toast.error('Erreur lors de la sauvegarde'); }
  };

  const handleDelete = async (audienceId) => {
    try {
      await api.deleteEmailAudience(audienceId);
      toast.success('Audience supprimée');
      await load();
      setPanel(null);
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  // Styles tableau
  const th = { padding: '8px 14px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid #E5E7EB', whiteSpace: 'nowrap' };
  const td = { padding: '8px 14px', fontSize: 13, color: '#111827', borderBottom: '1px solid #F3F4F6', cursor: 'pointer' };
  const tdNom = { ...td, padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap' };

  // Lookup rapide tag_id → label
  const allTagsMap = [...(tagsFonction || []), ...(tagsAutomation || [])].reduce((acc, t) => {
    acc[t.tag_id] = t.label || t.nom || t.tag_id;
    return acc;
  }, {});

  return (
    <div style={{ padding: '24px 32px', fontFamily: 'Inter, system-ui, sans-serif' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Audiences</h1>
          <p style={{ fontSize: 13, color: '#6B7280', margin: '4px 0 0' }}>Segments de contacts pour vos campagnes email</p>
        </div>
        <button onClick={() => setPanel({ mode: 'new', data: null })}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          data-testid="new-audience-btn">
          <Plus size={15} /> Nouvelle audience
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p style={{ color: '#9CA3AF', fontSize: 13 }}>Chargement…</p>
      ) : audiences.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0', color: '#9CA3AF' }}>
          <Users size={36} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ margin: 0, fontSize: 14 }}>Aucune audience créée</p>
        </div>
      ) : (
        <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={th}>Nom</th>
                <th style={th}>Description</th>
                <th style={th}>Tags</th>
                <th style={{ ...th, textAlign: 'center' }}>Contacts</th>
              </tr>
            </thead>
            <tbody>
              {audiences.map((a, i) => {
                const fr     = a.filter_rules || {};
                const tagIds = fr.tag_ids || [];
                return (
                  <tr key={a.audience_id} onClick={() => setPanel({ mode: 'edit', data: a })}
                    style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#FAFAFA'}
                    data-testid={`audience-row-${a.audience_id}`}>
                    <td style={tdNom}>{a.audience_name}</td>
                    <td style={{ ...td, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.description || '—'}
                    </td>
                    <td style={td}>
                      {tagIds.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {tagIds.map(tid => (
                            <span key={tid} style={{ background: '#FEF3C7', color: '#92400E', padding: '1px 7px', borderRadius: 99, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                              {allTagsMap[tid] || tid}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <span style={{ background: '#DBEAFE', color: '#1D4ED8', borderRadius: 99, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
                        {a.nb_contacts ?? 0}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Overlay + Side panel */}
      {panel && (
        <>
          <div onClick={() => setPanel(null)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 40 }} />
          <AudienceSidePanel
            initial={panel.data}
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => setPanel(null)}
            tagsFonction={tagsFonction}
            tagsAutomation={tagsAutomation}
          />
        </>
      )}
    </div>
  );
}
